import { useState, useEffect } from 'react';
import axios, { CancelToken } from 'axios';
import * as Sentry from '@sentry/browser';

import { STATUS } from '../constants';

const { IDLE, PENDING, SUCCESS, ERROR } = STATUS;

export function useAssetImages({ assetId, campsiteId, tourId }) {
    const initialState = {
        heroImage: {},
        galleryImages: [],
    };

    const [imagesObject, setImagesObject] = useState(initialState);
    const [status, setStatus] = useState(IDLE);

    useEffect(() => {
        let isMounted = true;
        const cancelTokenSource = CancelToken.source();
        // get the content type needed for the api URL
        let contentType;
        let id;
        const galleryItems = [];
        const heroItems = [];
        const previewItems = [];

        if (assetId) {
            contentType = 'asset';
            id = assetId;
        } else if (tourId) {
            contentType = 'tour';
            id = tourId;
        } else if (campsiteId) {
            contentType = 'campsite';
            id = campsiteId;
        }

        // Get images from api
        if (contentType && id) {
            setStatus(PENDING);

            const loadData = async () => {
                try {
                    const { data } = await axios.get(
                        `/api/media/public/${contentType}/${id}`,
                        {
                            cancelToken: cancelTokenSource.token,
                        }
                    );
                    if (!data || !!data.error) {
                        setStatus(ERROR);
                    } else {
                        const allImages = data?.result;

                        if (isMounted && Array.isArray(allImages)) {
                            allImages.forEach((item) => {
                                const {
                                    is_gallery: isGallery,
                                    is_primary: isHero,
                                    is_preview: isPreview,
                                    title,
                                    description,
                                    url,
                                    credits,
                                    position,
                                } = item;

                                if (isGallery && title && url) {
                                    galleryItems.push({
                                        altText: title,
                                        caption: description,
                                        url,
                                        credit: credits,
                                        position,
                                    });
                                }

                                if (isHero && title && url) {
                                    heroItems.push({
                                        altText: title,
                                        caption: description,
                                        url,
                                        credit: credits,
                                    });
                                }

                                if (isPreview && title && url) {
                                    previewItems.push({
                                        altText: title,
                                        caption: description,
                                        url,
                                        credit: credits,
                                    });
                                }
                            });

                            galleryItems.sort((a, b) => {
                                return a.position - b.position;
                            });

                            setImagesObject({
                                galleryImages: galleryItems,
                                heroImage: heroItems[0],
                                previewImage: previewItems[0],
                            });
                            setStatus(SUCCESS);
                        }
                        // We'll assume a non-array return means there are no
                        // images for the given id ... return SUCCESS and empty
                        // sets for everything
                        else if (isMounted) {
                            setImagesObject({
                                galleryImages: [],
                                heroImage: {},
                                previewImage: {},
                            });
                            setStatus(SUCCESS);
                        }
                    }
                } catch (error) {
                    if (!axios.isCancel(error)) {
                        Sentry.captureException(error);
                    }
                }
            };

            loadData();
        }

        return () => {
            isMounted = false;
            cancelTokenSource.cancel();
        };
    }, [assetId, tourId, campsiteId]);

    return { ...imagesObject, status };
}
